/* PDP presentation only. Commerce state and calculation live in dh-home.js. */
(() => {
  if (window.__dasheaPdp) return;
  window.__dasheaPdp = true;
  // Presentation only: a single index pairs each quotation with its photographs.
  class PurchaseProof extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const on = (node,event,fn) => node.addEventListener(event,fn,{signal});
      this.quotes = [...this.querySelectorAll('[data-proof-quote]')];
      this.portraits = [...this.querySelectorAll('[data-proof-portraits]')];
      if (this.quotes.length < 2 || this.quotes.length !== this.portraits.length) return;
      this.index = Math.min(this.index || 0,this.quotes.length - 1);
      this.editorPaused = false;
      this.focused = this.contains(document.activeElement); this.inView = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      // Keep a focused source link in place while someone uses the keyboard.
      on(this,'focusin',() => {this.focused=true;this.schedule();});
      on(this,'focusout',() => queueMicrotask(() => {if(!signal.aborted){this.focused=this.contains(document.activeElement);this.schedule();}}));
      on(document,'visibilitychange',() => this.schedule());
      on(this.motion,'change',() => this.schedule());
      on(document,'shopify:block:select',e => {
        const index=this.quotes.findIndex(q=>q.dataset.proofBlock===e.detail?.blockId);
        if(index>=0){this.editorPaused=true;this.go(index);}
      });
      on(document,'shopify:block:deselect',() => {this.editorPaused=false;this.schedule();});
      // A failed picture leaves its author's initial, never a broken image or a different review's face.
      this.querySelectorAll('.dh-proof-avatar img').forEach(img => {
        const check = () => {img.hidden=img.complete&&!img.naturalWidth;};
        on(img,'load',check);on(img,'error',check);check();
      });
      this.observer = new IntersectionObserver(entries => {
        this.inView=entries[0].isIntersecting;
        if(this.inView)this.querySelectorAll('.dh-proof-avatar img').forEach(img=>{img.loading='eager';});
        this.schedule();
      },{threshold:0});
      this.observer.observe(this);
      this.go(this.index);
    }
    go(index) {
      this.index = (index % this.quotes.length + this.quotes.length) % this.quotes.length;
      this.quotes.forEach((quote,i) => {
        const active=i===this.index;
        quote.toggleAttribute('data-active',active);
        quote.setAttribute('aria-hidden',String(!active));
        quote.tabIndex=active?0:-1;
        this.portraits[i].toggleAttribute('data-active',active);
      });
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);this.timer=null;
      if(!this.isConnected||!this.abort||this.abort.signal.aborted)return;
      if(this.editorPaused||this.focused||this.motion.matches||!this.inView||document.hidden)return;
      this.timer=setTimeout(()=>this.go(this.index+1),Number(this.dataset.interval)||5000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);this.timer=null;
      this.observer?.disconnect();this.abort?.abort();this.abort=null;
    }
  }
  if (!customElements.get('dashea-purchase-proof')) customElements.define('dashea-purchase-proof',PurchaseProof);
  // Muted gallery and day/night clips share visibility, pause and reduced-motion handling.
  class GalleryVideo extends HTMLElement {
    connectedCallback() {
      if(this.abort)return;
      this.abort=new AbortController();const signal=this.abort.signal;
      const on=(node,event,fn,options={})=>node.addEventListener(event,fn,{...options,signal});
      this.video=this.querySelector('video');this.toggle=this.querySelector('[data-gallery-video-toggle]');
      const slide=this.closest('[data-pdp-slide]');
      this.active=slide?slide.hasAttribute('data-gallery-active'):!this.closest('[data-daypart-panel]')?.hidden;
      this.inView=false;this.covered=false;this.userPaused=false;this.manual=false;this.blocked=false;this.interrupted=false;
      // An initial deep anchor may scroll after load. Arm autoplay only once that
      // trip has taken this gallery off screen; a later return can then play it.
      const entryTarget=document.getElementById(location.hash.slice(1));
      this.landingReady=!entryTarget||!!this.closest('.dh-pdp-main,.dh-pdp-routine')?.contains(entryTarget);
      this.motion=matchMedia('(prefers-reduced-motion: reduce)');
      this.video.controls=false;this.video.muted=true;this.video.defaultMuted=true;this.toggle.hidden=false;
      on(this.toggle,'click',()=>{
        this.landingReady=true;
        if(!this.video.paused){this.userPaused=true;this.manual=false;}
        else{this.userPaused=false;this.manual=true;this.blocked=false;this.interrupted=false;}
        this.sync();
      });
      const paint=()=>{
        this.toggle.setAttribute('aria-label',this.video.paused?this.dataset.playLabel:this.dataset.pauseLabel);
        this.querySelector('[data-video-play]').hidden=!this.video.paused;
        this.querySelector('[data-video-pause]').hidden=this.video.paused;
      };
      on(this.video,'pause',paint);on(this.video,'playing',paint);
      on(this.video,'play',()=>{
        if(!this.shouldPlay()){this.video.pause();return;}
        document.querySelectorAll('video').forEach(other=>{if(other!==this.video)other.pause();});
        paint();
      });
      on(document,'play',event=>{if(event.target!==this.video&&!this.video.paused){this.interrupted=true;this.video.pause();}},{capture:true});
      on(document,'visibilitychange',()=>this.sync());on(this.motion,'change',()=>this.sync());
      this.observer=new IntersectionObserver(entries=>{
        const visible=entries[0].isIntersecting&&entries[0].intersectionRatio>=.2;
        if(!visible&&entries[0].boundingClientRect.height>0)this.landingReady=true;
        if(visible&&!this.inView)this.interrupted=false;
        this.inView=visible;this.sync();
      },{threshold:[0,.2]});
      this.observer.observe(this);paint();
    }
    setActive(active){if(active&&!this.active)this.interrupted=false;if(!active)this.manual=false;this.active=active;this.sync();}
    setCovered(covered){this.covered=covered;this.sync();}
    shouldPlay(){return this.isConnected&&this.landingReady&&this.active&&this.inView&&!this.covered&&!document.hidden&&!this.userPaused&&!this.blocked&&!this.interrupted&&(this.manual||(this.dataset.autoplay==='true'&&!this.motion.matches&&!navigator.connection?.saveData));}
    sync(){
      if(!this.video)return;
      // preload=none keeps an offscreen entry on its poster; fast-start MP4 can play from the first byte range.
      const play=this.shouldPlay();this.video.autoplay=!!play;
      if(!play){this.video.pause();return;}
      if(this.video.paused&&!this.pending){
        this.pending=this.video.play().catch(error=>{if(error.name!=='AbortError')this.blocked=true;}).finally(()=>{this.pending=null;if(!this.shouldPlay())this.video.pause();else if(this.video.paused)this.sync();});
      }
    }
    disconnectedCallback(){this.video?.pause();this.observer?.disconnect();this.abort?.abort();this.abort=null;}
  }
  if(!customElements.get('dashea-gallery-video'))customElements.define('dashea-gallery-video',GalleryVideo);
  class Gallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort=new AbortController(); const signal=this.abort.signal;
      this.rail=this.querySelector('[data-pdp-gallery-rail]'); this.slides=[...this.querySelectorAll('[data-pdp-slide]')]; this.thumbs=[...this.querySelectorAll('[data-pdp-thumb]')];
      this.dialog=this.querySelector('[data-gallery-dialog]'); this.infoDialog=this.querySelector('[data-pdp-info-dialog]'); this.index=0;
      const on=(node,event,fn,options={})=>node.addEventListener(event,fn,{...options,signal});
      this.thumbs.forEach((a,i)=>on(a,'click',event=>{event.preventDefault();this.go(i);}));
      this.querySelectorAll('img').forEach(img=>img.draggable=false);
      on(this.rail,'scroll',()=>{
        if(this.animating)return;
        this.update(this.nearest());
      },{passive:true});
      on(this.rail,'wheel',()=>this.stopMotion(),{passive:true});
      // Native horizontal scrolling owns touch gestures. Never capture the pointer
      // or write scrollLeft while a finger is moving; CSS snap settles the rail.
      on(this.rail,'pointerdown',()=>this.stopMotion(),{passive:true});
      on(this.rail,'keydown',event=>{
        if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)||event.target.closest('video'))return;
        event.preventDefault();this.go(event.key==='Home'?0:event.key==='End'?this.slides.length-1:this.index+(event.key==='ArrowRight'?1:-1));
      });
      on(this.rail,'click',event=>{const link=event.target.closest('[data-pdp-zoom]');if(link){event.preventDefault();this.open(this.slides.indexOf(link.closest('[data-pdp-slide]')),link);}});
      on(this.querySelector('[data-gallery-prev]'),'click',()=>this.go(this.index-1));
      on(this.querySelector('[data-gallery-next]'),'click',()=>this.go(this.index+1));
      this.querySelector('[data-gallery-controls]').hidden=false;
      on(this.querySelector('[data-lightbox-close]'),'click',()=>this.dialog.close());
      on(this.dialog,'close',()=>{this.coverVideo(false);document.documentElement.classList.remove('dh-pdp-modal-open');(this.slides[this.lightboxIndex]?.querySelector('[data-pdp-zoom]')||this.returnFocus)?.focus({preventScroll:true});});
      on(this.dialog,'click',event=>{if(event.target===this.dialog||event.target.closest('a[href^="#"]'))this.dialog.close();});
      on(this.querySelector('[data-lightbox-prev]'),'click',()=>this.moveLightbox(-1));
      on(this.querySelector('[data-lightbox-next]'),'click',()=>this.moveLightbox(1));
      on(this.dialog,'keydown',event=>{if(!this.dialog.hasAttribute('data-zoomed')&&['ArrowLeft','ArrowRight'].includes(event.key)){event.preventDefault();this.moveLightbox(event.key==='ArrowRight'?1:-1);}});
      const stage=this.querySelector('[data-lightbox-stage]');
      on(stage,'dblclick',()=>this.setZoom(!this.dialog.hasAttribute('data-zoomed')));
      on(stage,'keydown',event=>{if(event.target===stage&&['Enter',' '].includes(event.key)){event.preventDefault();this.setZoom(!this.dialog.hasAttribute('data-zoomed'));}});
      on(stage,'pointerdown',event=>{if(event.pointerType==='mouse'&&event.button===0&&this.dialog.hasAttribute('data-zoomed')){this.pan={id:event.pointerId,x:event.clientX,y:event.clientY,left:stage.scrollLeft,top:stage.scrollTop};stage.setPointerCapture(event.pointerId);event.preventDefault();}});
      on(stage,'pointermove',event=>{const p=this.pan;if(p&&event.pointerId===p.id){stage.scrollLeft=p.left+p.x-event.clientX;stage.scrollTop=p.top+p.y-event.clientY;}});
      on(stage,'pointerup',()=>{this.pan=null;});on(stage,'pointercancel',()=>{this.pan=null;});
      const ingredients=this.querySelector('[data-pdp-ingredients-link]');
      const ingredientDetails=document.getElementById(ingredients.hash.slice(1));
      if(ingredientDetails)on(ingredientDetails,'toggle',()=>{if(ingredientDetails.open)this.fillIngredientOverview(ingredientDetails);});
      on(ingredients,'click',event=>{const details=document.getElementById(ingredients.hash.slice(1));if(!details)return;event.preventDefault();const body=this.querySelector('[data-info-body]');body.innerHTML=details.querySelector('.dh-pdp-detail-body').innerHTML;this.fillIngredientOverview(body);this.infoDialog.showModal();this.coverVideo(true);document.documentElement.classList.add('dh-pdp-modal-open');});
      on(this.querySelector('[data-info-close]'),'click',()=>this.infoDialog.close());
      on(this.infoDialog,'close',()=>{this.coverVideo(false);document.documentElement.classList.remove('dh-pdp-modal-open');ingredients.focus({preventScroll:true});});
      on(this.infoDialog,'click',event=>{if(event.target===this.infoDialog)this.infoDialog.close();if(event.target.closest('a[href^="#"]'))this.infoDialog.close();});
      on(this,'shopify:block:select',event=>{const i=this.slides.findIndex(s=>s.id.endsWith('-'+event.detail.blockId));if(i>=0)this.go(i);});
      this.rail.dataset.enhanced='true';
      this.resize=new ResizeObserver(()=>this.go(this.index,'instant'));this.resize.observe(this.rail);this.update(0);
    }
    behavior(){return matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth';}
    fillIngredientOverview(container){
      const slot=container.querySelector('[data-ingredient-overview-slot]');
      const source=document.querySelector('.dh-pdp-ingredients [data-ingredient-overview]');
      if(!slot||!source)return; // The original text + complete INCI remains the no-JS / missing-section fallback.
      const overview=source.cloneNode(true);
      overview.classList.add('dh-ingredient-overview--compact');
      overview.removeAttribute('data-ingredient-overview');
      overview.querySelectorAll('*').forEach(node=>{
        for(const attribute of [...node.attributes])if(attribute.name.startsWith('data-shopify-'))node.removeAttribute(attribute.name);
      });
      slot.replaceChildren(overview);
    }
    nearest(){return this.slides.reduce((best,s,i)=>Math.abs(s.offsetLeft-this.slides[0].offsetLeft-this.rail.scrollLeft)<Math.abs(this.slides[best].offsetLeft-this.slides[0].offsetLeft-this.rail.scrollLeft)?i:best,0);}
    stopMotion(){cancelAnimationFrame(this.motionFrame);this.animating=false;if(this.rail)delete this.rail.dataset.animating;}
    go(i,behavior=this.behavior()){
      this.stopMotion();
      i=Math.max(0,Math.min(i,this.slides.length-1));
      const from=this.rail.scrollLeft,to=this.slides[i].offsetLeft-this.slides[0].offsetLeft,distance=to-from;
      this.update(i);
      if(behavior!=='smooth'||Math.abs(distance)<1){this.rail.scrollLeft=to;return;}
      // Preserve the existing arrow easing. Native snap is off only for this
      // explicit button/keyboard animation, never for finger scrolling.
      const started=performance.now(),duration=Math.min(440,280+Math.abs(distance)/this.rail.clientWidth*100);
      this.animating=true;this.rail.dataset.animating='true';
      const frame=now=>{
        const progress=Math.min(1,(now-started)/duration);
        this.rail.scrollLeft=from+distance*(1-Math.pow(1-progress,3));
        if(progress<1)this.motionFrame=requestAnimationFrame(frame);
        else{this.animating=false;delete this.rail.dataset.animating;this.update(i);}
      };
      this.motionFrame=requestAnimationFrame(frame);
    }
    update(i){this.index=i;this.thumbs.forEach((a,n)=>n===i?a.setAttribute('aria-current','true'):a.removeAttribute('aria-current'));this.slides.forEach((s,n)=>{s.toggleAttribute('data-gallery-active',n===i);s.querySelector('dashea-gallery-video')?.setActive(n===i);if(n!==i)s.querySelectorAll('video').forEach(v=>v.pause());});this.querySelector('[data-gallery-count]').textContent=`${i+1} / ${this.slides.length}`;this.querySelector('[data-gallery-prev]').disabled=i===0;this.querySelector('[data-gallery-next]').disabled=i===this.slides.length-1;}
    coverVideo(covered){this.querySelectorAll('dashea-gallery-video').forEach(clip=>clip.setCovered(covered));}
    open(i,trigger){if(!this.slides[i]?.querySelector('[data-pdp-zoom]'))return;this.returnFocus=trigger;this.lightboxIndex=i;document.querySelectorAll('.dh-video').forEach(v=>v.pause());this.showImage();this.dialog.showModal();this.coverVideo(true);document.documentElement.classList.add('dh-pdp-modal-open');}
    moveLightbox(direction){let i=this.lightboxIndex+direction;while(i>=0&&i<this.slides.length&&!this.slides[i].querySelector('[data-pdp-zoom]'))i+=direction;if(i>=0&&i<this.slides.length){this.lightboxIndex=i;this.go(i,'instant');this.showImage();}}
    showImage(){const slide=this.slides[this.lightboxIndex],link=slide.querySelector('[data-pdp-zoom]'),img=this.querySelector('[data-lightbox-image]')||document.createElement('img');if(!img.parentElement){img.dataset.lightboxImage='';img.width=1400;img.height=1400;this.querySelector('.dh-pdp-lightbox__canvas').append(img);}img.src=link.href;img.alt=link.querySelector('img').alt;this.querySelector('[data-lightbox-note]').hidden=!slide.querySelector('[data-gallery-note]');this.querySelector('[data-lightbox-count]').textContent=`${this.lightboxIndex+1} / ${this.slides.length}`;this.querySelector('[data-lightbox-prev]').disabled=!this.slides.slice(0,this.lightboxIndex).some(s=>s.querySelector('[data-pdp-zoom]'));this.querySelector('[data-lightbox-next]').disabled=!this.slides.slice(this.lightboxIndex+1).some(s=>s.querySelector('[data-pdp-zoom]'));this.setZoom(false);}
    setZoom(zoomed){this.dialog.toggleAttribute('data-zoomed',zoomed);const stage=this.querySelector('[data-lightbox-stage]');stage.setAttribute('aria-label',zoomed?stage.dataset.zoomOut:stage.dataset.zoomIn);stage.scrollLeft=zoomed?(stage.scrollWidth-stage.clientWidth)/2:0;stage.scrollTop=zoomed?(stage.scrollHeight-stage.clientHeight)/2:0;}
    disconnectedCallback(){this.stopMotion();if(this.rail)delete this.rail.dataset.enhanced;if(this.dialog?.open)this.dialog.close();if(this.infoDialog?.open)this.infoDialog.close();this.resize?.disconnect();this.abort?.abort();this.abort=null;}
  }
  if (!customElements.get('dashea-pdp-gallery')) customElements.define('dashea-pdp-gallery',Gallery);
  class Dayparts extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.tabs = [...this.querySelectorAll('[data-daypart-tab]')];
      this.panels = [...this.querySelectorAll('[data-daypart-panel]')];
      this.querySelector('[data-daypart-tabs]').setAttribute('role','tablist');
      this.tabs.forEach((tab,index) => {
        tab.setAttribute('role','tab'); tab.setAttribute('aria-controls',tab.hash.slice(1));
        tab.addEventListener('click',event => {event.preventDefault(); this.select(tab.dataset.daypartTab);}, {signal});
        tab.addEventListener('keydown',event => {
          if (event.key === ' ') {event.preventDefault(); this.select(tab.dataset.daypartTab); return;}
          if (!['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(this.tabs[next].dataset.daypartTab); this.tabs[next].focus({preventScroll:true});
        }, {signal});
      });
      this.panels.forEach(panel => {panel.setAttribute('role','tabpanel'); panel.tabIndex=0;});
      this.querySelectorAll('[data-daypart-switch]').forEach(link => link.addEventListener('click',event => {
        event.preventDefault(); this.select(link.dataset.daypartSwitch);
        this.tabs.find(tab=>tab.dataset.daypartTab===link.dataset.daypartSwitch)?.focus({preventScroll:true});
      }, {signal}));
      this.addEventListener('shopify:block:select',event => {
        const block = [...this.querySelectorAll('[data-daypart-block]')].find(block=>block.dataset.daypartBlock===event.detail.blockId);
        if(block) this.select(block.closest('[data-daypart-panel]').dataset.daypartPanel);
      }, {signal});
      this.dataset.enhanced='true';
      const linkedPanel=this.panels.find(panel=>'#'+panel.id===location.hash);
      this.select(linkedPanel?.dataset.daypartPanel || 'day');
    }
    select(period) {
      this.panels.forEach(panel => {
        const active = panel.dataset.daypartPanel === period;
        if(!active) panel.querySelectorAll('video').forEach(video=>video.pause());
        panel.hidden = !active;
        panel.querySelectorAll('dashea-gallery-video').forEach(clip=>clip.setActive(active));
      });
      this.tabs.forEach(tab => {
        const active=tab.dataset.daypartTab===period;
        tab.setAttribute('aria-selected',String(active)); tab.tabIndex=active?0:-1;
      });
    }
    disconnectedCallback() {
      this.querySelectorAll('video').forEach(video=>video.pause());
      this.abort?.abort(); this.abort=null;
    }
  }
  if (!customElements.get('dashea-dayparts')) customElements.define('dashea-dayparts',Dayparts);
  class Reviews extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.cards = [...this.querySelectorAll('[data-review-card]')];
      this.filters = [...this.querySelectorAll('[data-review-filter]')];
      this.topic = 'all'; this.page = 0;
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.status = this.querySelector('[data-review-status]');
      this.sizes = [matchMedia('(min-width: 1024px)')];
      this.pageSize = () => this.sizes[0].matches ? 3 : 2;
      this.filters.forEach(button => button.addEventListener('click', () => {
        this.topic = button.dataset.reviewFilter; this.page = 0; this.render();
      }, {signal}));
      this.previous.addEventListener('click', () => {this.page--; this.render();}, {signal});
      this.next.addEventListener('click', () => {this.page++; this.render();}, {signal});
      this.sizes.forEach(size => size.addEventListener('change', () => {this.page = 0; this.render();}, {signal}));
      // Selecting a hidden review in the Shopify editor reveals that exact block.
      this.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.topic = 'all'; this.page = Math.floor(index / this.pageSize()); this.render();
      }, {signal});
      this.querySelector('[data-review-filters]').hidden = false;
      this.querySelector('[data-review-pagination]').hidden = false;
      this.render();
    }
    render() {
      const matches = this.cards.filter(card => this.topic === 'all' || card.dataset.reviewTopics.split(' ').includes(this.topic));
      const size = this.pageSize();
      this.page = Math.max(0, Math.min(this.page, Math.ceil(matches.length / size) - 1));
      const start = this.page * size, visible = matches.slice(start, start + size);
      this.cards.forEach(card => {card.hidden = !visible.includes(card);});
      this.filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.reviewFilter === this.topic)));
      // aria-disabled keeps keyboard focus stable when reaching either end.
      this.previous.setAttribute('aria-disabled', String(this.page === 0));
      this.next.setAttribute('aria-disabled', String(start + size >= matches.length));
      this.status.textContent = `${matches.length ? start + 1 : 0}–${Math.min(start + size, matches.length)} / ${matches.length} ${this.status.dataset.countLabel}`;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.cards?.forEach(card => {card.hidden = false;});
    }
  }
  if (!customElements.get('dashea-pdp-reviews')) customElements.define('dashea-pdp-reviews', Reviews);
  function attach() {
    const bundle=document.querySelector('dashea-bundle[data-primary-key]');
    if(!bundle)return;
    const apply = state => {
      if(!state)return;
      document.querySelectorAll('[data-pdp-count]').forEach(n=>n.textContent=state.unavailable?'Tato maska je vyprodaná':state.count);
      document.querySelectorAll('[data-pdp-mode]').forEach(n=>n.textContent=state.mode);
      document.querySelectorAll('[data-pdp-price]').forEach(n=>n.textContent=state.price);
      document.querySelectorAll('[data-pdp-renewal]').forEach(n=>n.textContent=state.renewal);
    };
    if(!bundle._pdpAttached){bundle.addEventListener('dashea:selection',e=>apply(e.detail));bundle._pdpAttached=true;}
    apply(bundle.selection);
    const sticky=document.querySelector('[data-pdp-sticky]');
    const button=bundle.querySelector('[data-bundle-add]');
    if(!sticky||!button)return;
    const footer=document.querySelector('#dh-footer')||document.querySelector('footer');
    bundle._pdpObserver?.disconnect();
    bundle._pdpStickyAbort?.abort();
    bundle._pdpStickyAbort=new AbortController();
    let stickyFrame=0;
    const updateSticky=()=>{
      if(stickyFrame)return;
      stickyFrame=requestAnimationFrame(()=>{
        stickyFrame=0;
        if(!button.isConnected)return;
        const footerVisible=footer&&footer.getBoundingClientRect().top<window.innerHeight;
        sticky.hidden=button.getBoundingClientRect().bottom>0||footerVisible;
      });
    };
    // An anchor can jump from below to above the viewport without changing
    // intersection. Scroll updates cover that case and restore bfcache state.
    bundle._pdpObserver=new IntersectionObserver(updateSticky,{threshold:0});
    bundle._pdpObserver.observe(button);
    if(footer)bundle._pdpObserver.observe(footer);
    for(const event of ['scroll','resize','pageshow'])window.addEventListener(event,updateSticky,{passive:true,signal:bundle._pdpStickyAbort.signal});
    updateSticky();
    document.querySelectorAll('a[href="#nabidka"]').forEach(a=>{
      if(a.dataset.pdpReturnReady)return;a.dataset.pdpReturnReady='true';
      a.addEventListener('click',()=>{document.getElementById('nabidka')?.focus({preventScroll:true});});
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',attach,{once:true});else attach();
  document.addEventListener('shopify:section:load',attach);
})();
